import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate hook
import { motion } from 'framer-motion';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import TabbedFormComponent from '../common/TabbedFormComponent'
import AdvancedDataTableComponent from '../common/AdvancedDataTableComponent'
import { registerUser, fetchUserDataByClient, fetchUserDataById, updateUser } from '../../services/userApiService'; // Adjust the path as necessary
import { fetchClientData } from '../../services/adminApiService';
import { fetchRoleData } from '../../services/roleApiService';
import FloatingLabelInput from '../ui/FloatingLabelInput';
import FloatingLabelSelect from '../ui/FloatingLabelSelect';


const UserForm = ({ selectedUser, onUserUpdated }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loadingStates, setLoadingStates] = useState(false);
    const [users, setUsers] = useState([]);

    const [formValues, setFormValues] = useState({
        userId: '',
        clientId: '',
        roleId: '',
        userName: '',
        email: '',
        personName: '',
        password: '',
        confirmPassword: '',
        phoneNumber: '',
        reportingToUserId: ''
    });

    const [errors, setErrors] = useState({});

    // Simple validation function
    const validate = () => {
        const newErrors = {};
        if (!formValues.userName) newErrors.userName = 'User Name is required';
        if (!formValues.clientId) newErrors.clientId = 'Organization is required';
        if (!formValues.roleId) newErrors.roleId = 'Role is required';
        if (!formValues.personName) newErrors.personName = 'Person Name is required';
        if (!/^[0-9]{10}$/.test(formValues.phoneNumber)) {
            newErrors.phoneNumber = 'Phone number must be exactly 10 digits';
        }
        if (!/\S+@\S+\.\S+/.test(formValues.email)) {
            newErrors.email = 'Email is required and must be valid';
        }
        if (!formValues.password) newErrors.password = 'Password is required';
        if (!formValues.confirmPassword) newErrors.confirmPassword = 'Confirm Password is required';
        if (!(formValues.password === formValues.confirmPassword)) newErrors.confirmPassword = 'password & Confirm password Mismatch';
        return newErrors;
    };

    const handleBlur = (field) => {
        const validationErrors = validate();
        setErrors(validationErrors);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
        // Validate each field in real-time
        //const validationErrors = validate(); // Re-validate all fields
        //setErrors({ ...errors, [name]: '' }); // Clear error for the field being changed
        // setErrors(validationErrors); // Update errors state with any validation messages

    };

    const getClients = async () => {
        try {
            const clientData = await fetchClientData();
            setClients(clientData.map(client => ({ value: client.clientId, label: client.clientName })));
        } catch (error) {
            console.error("Error fetching countries:", error);
        }
    };


    const inputClasses = (field) =>
        errors[field] ? 'border-red-500' : 'border-gray-300';


    useEffect(() => {
        getClients();
    }, []);

    const fetchRolesByClient = async (clientId) => {
        try {
            const roleData = await fetchRoleData(clientId);
            setRoles(roleData.map(role => ({ value: role.roleId, label: role.roleName })));
        } catch (error) {
            console.error("Error fetching states:", error);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if (selectedUser && selectedUser.userId != "") {
                setIsEditing(true);
                setFormValues({
                    userId: selectedUser.userId || '',
                    clientId: selectedUser.clientId || '',
                    roleId: selectedUser.roleId || '',
                    userName: selectedUser.userName || '',
                    personName: selectedUser.personName || '',
                    email: selectedUser.email || '',
                    password: selectedUser.password || '',
                    confirmPassword: selectedUser.password || '',
                    phoneNumber: selectedUser.phoneNumber || '',
                    reportingToUserId: selectedUser.reportingToUserId || ''
                });
                setFormValues(prevValues => ({
                    ...prevValues,
                    clientId: selectedUser.clientId,
                    roleId: selectedUser.roleId,
                }));
                if (selectedUser.clientId) {
                    fetchRolesByClient(selectedUser.clientId).then(() => {
                        setFormValues(prevValues => ({
                            ...prevValues,
                            roleId: selectedUser.roleId || null
                        }));
                    });
                    fetchUsersByClient(selectedUser.clientId).then(() => {
                        setFormValues(prevValues => ({
                            ...prevValues,
                            reportingToUserId: selectedUser.reportingToUserId || null
                        }));
                    });
                } else {
                    setRoles([]); // Clear Roles if no ClientId is selected
                    setUsers([]);
                }
            } else {
                setIsEditing(false);
            }
        }, 500); // 3000ms = 3 seconds

        return () => clearTimeout(timer);
    }, [selectedUser, clients]); // Ensure to re-run when countries change

    const fetchUsersByClient = async (clientId) => {
        try {
            const userData = await fetchUserDataByClient(clientId);
            setUsers(userData.map(user => ({ value: user.userId, label: user.userName })));
        } catch (error) {
            console.error("Error fetching clients:", error);
        }
    };


    const handleSubmit = async (e) => {

        e.preventDefault();
        const validationErrors = validate();
        // const isValid = await handleValidation(formValues);
        // if (!isValid) return; 
        if (Object.keys(validationErrors).length === 0) {
            try {
                // await validationSchema.validate(formValues, { abortEarly: false });

                //console.log('Form submitted', formValues);
                // setErrors({});
                setLoading(true);
                let data;
                if (isEditing) {
                    data = await updateUser(formValues);
                } else {
                    data = await registerUser(formValues);
                }

                setApiResponse(data);
                if (!data.error) {
                    resetFormValues();
                    onUserUpdated(null);
                    setIsEditing(false);
                    //setClients([]); 
                    setRoles([]);
                } else {
                    return;
                }
            } catch (err) {
                const validationErrors = {};
                err.inner.forEach(error => {
                    validationErrors[error.path] = error.message;
                });
                setErrors(validationErrors);
                setApiResponse({ err: 'Failed to submit form. Please try again.' });
            } finally {
                setLoading(false);
            }
            setErrors({});
        } else {
            setErrors(validationErrors);
        }
    };
    const resetFormValues = () => {
        setFormValues({
            userId: '',
            clientId: '',
            roleId: '',
            userName: '',
            email: '',
            personName: '',
            password: '',
            confirmPassword: '',
            phoneNumber: '',
            reportingToUserId: ''
        });
        setIsEditing(false);
        setErrors({});
        onUserUpdated(null);
        //setApiResponse(null);
    }
    const navigate = useNavigate(); // Initialize useNavigate

    const handleCancel = () => {
        resetFormValues();
        onUserUpdated(null);
        setRoles([]);
        // navigate('/clientRegistration'); // Replace with your desired URL
        // window.location.reload();
    };
    return (
        <div className="container mx-auto p-6 bg-white rounded-lg border">
            <form onSubmit={handleSubmit} className="space-y-6 w-full">
                <div className="grid md:grid-cols-3 gap-4">
                    {isEditing ?
                        <input
                            type="hidden"
                            name="userId"
                            value={formValues.userId}
                            id="userId"
                            autoComplete="off"
                            readOnly={true}
                            onChange={handleChange}
                        /> : ''
                    }

<div className=" md:grid-cols-3">
                            {loading ? (
                                <p>Loading countries...</p>
                            ) : (
                                <FloatingLabelSelect
                                    label="Select Organization"
                                    id="clientId"
                                    name="clientId"
                                    value={clients.find(client => client.value === formValues.clientId) || null} // Handle no selection case with null
                                    onChange={(option) => {
                                        setFormValues({ ...formValues, clientId: option.value, roleId: '' }); // Reset stateId
                                        fetchRolesByClient(option.value); 
                                        fetchUsersByClient(option.value);// Fetch states for selected country
                                    }}
                                    onBlur={() => handleBlur('clientId')}
                                    className={inputClasses('clientId')}
                                    options={clients}
                                    isSearchable={true}
                                />
                            )}
                            {errors.clientId && <div className="text-red-500 text-sm">{errors.clientId}</div>}
                            {/* <input value={JSON.stringify(countries.find(country => country.value === formValues.countryId) || 'ACD')}/> */}

                        </div>
                    <div className=" md:grid-cols-3">
                        <FloatingLabelInput
                            label="User Name"
                            id="userName"
                            name="userName"
                            value={formValues.userName}
                            onChange={handleChange}
                            onBlur={() => handleBlur('userName')}
                            className={inputClasses('userName')}

                        />
                        {errors.userName && <div className="text-red-500 text-sm">{errors.userName}</div>}
                    </div>

                    <div className=" md:grid-cols-3">
                        <FloatingLabelInput
                            label="Password"
                            type="password"
                            id="password"
                            name="password"
                            value={formValues.password}
                            onChange={handleChange}
                            onBlur={() => handleBlur('password')}
                            className={inputClasses('password')}

                        />
                        {errors.password && <div className="text-red-500 text-sm">{errors.password}</div>}
                    </div>

                    <div className=" md:grid-cols-3">
                        <FloatingLabelInput
                            label="Confirm Password"
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formValues.confirmPassword}
                            onChange={handleChange}
                            onBlur={() => handleBlur('confirmPassword')}
                            className={inputClasses('confirmPassword')}

                        />
                        {errors.confirmPassword && <div className="text-red-500 text-sm">{errors.confirmPassword}</div>}
                    </div>

                    <div className=" md:grid-cols-3">
                        <FloatingLabelInput
                            label="Person Name"
                            id="personName"
                            name="personName"
                            value={formValues.personName}
                            onChange={handleChange}
                            onBlur={() => handleBlur('personName')}
                            className={inputClasses('personName')}

                        />
                        {errors.personName && <div className="text-red-500 text-sm">{errors.personName}</div>}
                    </div>
                    <div className=" md:grid-cols-3">
                        <FloatingLabelInput
                            label="Email"
                            id="email"
                            name="email"
                            value={formValues.email}
                            onChange={handleChange}
                            onBlur={() => handleBlur('email')}
                            className={inputClasses('email')}
                        />
                        {errors.email && <div className="text-red-500 text-sm">{errors.email}</div>}
                    </div>

                    <div className=" md:grid-cols-3">
                        <FloatingLabelInput
                            label="Phone Number"
                            id="phoneNumber"
                            name="phoneNumber"
                            value={formValues.phoneNumber}
                            onChange={handleChange}
                            onBlur={() => handleBlur('phoneNumber')}
                            className={inputClasses('phoneNumber')}

                        />
                        {errors.phoneNumber && <div className="text-red-500 text-sm">{errors.phoneNumber}</div>}
                    </div>



                        <div className=" md:grid-cols-3">
                            <>
                                {loadingStates ? (
                                    <p>Loading states...</p>
                                ) : (
                                    <FloatingLabelSelect
                                        label="Select Role"
                                        id="roleId"
                                        name="roleId"
                                        value={roles.find(role => role.value === formValues.roleId) || null} // Use null if no state is selected
                                        onChange={(option) => setFormValues({ ...formValues, roleId: option.value })}

                                        onBlur={() => handleBlur('roleId')}
                                        className={inputClasses('roleId')}
                                        options={roles}
                                        isSearchable={true}
                                    />

                                )}
                            </>
                            {errors.roleId && <div className="text-red-500 text-sm">{errors.roleId}</div>}
                        </div>

                        <div className=" md:grid-cols-3">
                            <>
                                {loadingStates ? (
                                    <p>Loading states...</p>
                                ) : (
                                    <FloatingLabelSelect
                                        label="Reporting To"
                                        id="reportingToUserId"
                                        name="reportingToUserId"
                                        value={users.find(user => user.value === formValues.reportingToUserId) || null} // Use null if no state is selected
                                        onChange={(option) => setFormValues({ ...formValues, reportingToUserId: option.value })}

                                        onBlur={() => handleBlur('reportingToUserId')}
                                        className={inputClasses('reportingToUserId')}
                                        options={users}
                                        isSearchable={true}
                                    />

                                )}
                            </>
                            {errors.reportingToUserId && <div className="text-red-500 text-sm">{errors.reportingToUserId}</div>}
                        </div>


                        <div className=" md:grid-cols-3">
                            <div className="mt-4">
                                <button
                                    type="submit"
                                    className={`px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 ${loading ? 'opacity-50' : ''}`}
                                    disabled={loading}
                                >
                                    {loading ? 'Submitting...' : selectedUser ? 'Update' : 'Submit'}
                                </button>
                            </div>
                            <div className="mt-4">
                                <button
                                    type="button"
                                    onClick={handleCancel} // This is how you reset the form
                                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                                >
                                    Cancel
                                </button>
                            </div>
                            <div className="mt-4"></div>
                        </div>
                    </div>
                    {apiResponse && (
                        <motion.div
                            className={`mt-4 flex items-center ${apiResponse.error ? 'text-red-500' : 'text-green-500'}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            {apiResponse.error ? (
                                <>
                                    <FaExclamationCircle className="mr-2" />
                                    {apiResponse.error}
                                </>
                            ) : (
                                <>
                                    <FaCheckCircle className="mr-2" />
                                    {apiResponse.success}
                                </>
                            )}
                        </motion.div>
                    )}
            </form>
        </div>
    );
};

const UserListTable = ({ onSelectUser, switchToFormTab }) => {
    const tableHeaders = ["S.No.", "User Name","Person Name", "Organization Name", "Role", "Email", "User Code", "Phone Number","Reporting Person", "CreatedAt"];

    const [userList, setUserList] = useState([]);  // Store Clients

    const [loading, setLoading] = useState(false);

    // Fetch Clients when the component loads
    useEffect(() => {
        const getUserData = async (clientId) => {
            try {
                const uses_Data = await fetchUserDataByClient(localStorage.getItem('clientID'));  // Fetch countries from service
                const userOptions = uses_Data.map((user, index) => ({
                    sno: index + 1,
                    id: user.userId,
                    personName: user.personName,
                    userName: user.userName,
                    clientName: user.clientName,
                    roleName: user.roleName,
                    email: user.email,
                    userCode: user.userCode,
                    phoneNumber: user.phoneNumber,
                    reportingToUserId: user.reportingToUserId,
                    reportingToUserName: user.reportingToUserName,
                    createdAt: user.createdDate
                }));
                setUserList(userOptions);  // Set the countries as options for the select dropdown

            } catch (error) {
                console.error("Error fetching countries:", error);
            } finally {
                // setLoadingCountries(false);  // Stop loading spinner
            }
        };

        getUserData();
    }, []);


    // Mapping of headers to data keys
    const headerMapping = {
        "S.No.": "sno",
        "User Name": "userName",
        "Organization Name": "clientName",
        "Role": "roleName",
        "Email": "email",
        "User Code": "userCode",
        "Phone Number": "phoneNumber",
        "Person Name":"personName",
        "Reporting Person":"reportingToUserName",
        "CreatedAt": "createdAt"
    };


    const handleEdit = async (rowObject) => {
        try {
            setLoading(true); // Show loading spinner

            // Fetch client data based on the row object (e.g., client ID)
            const user = await fetchUserDataById(rowObject.id);
            var userData = ({
                userId: user.userId,
                clientId: user.clientId,
                roleId: user.roleId,
                personName: user.personName,
                userName: user.userName,
                email: user.email,
                password: user.password,
                confirmPassword: user.password,
                phoneNumber: user.phoneNumber,
                reportingToUserId: user.reportingToUserId,
                reportingToUserName: user.reportingToUserName,
                status: user.status,

            });
            // Populate the form with the fetched client data
            onSelectUser(userData);

            // Switch to the form tab
            switchToFormTab();

        } catch (error) {
            console.error('Error fetching User data:', error);
        } finally {
            setLoading(false); // Hide loading spinner
        }
    };


    return (
        <div className="container mx-auto p-6 bg-white rounded-lg border">

            <AdvancedDataTableComponent
                headers={tableHeaders}
                data={userList}
                headerMapping={headerMapping}
                onEdit={handleEdit}
                showActions={true}
            />
        </div>
    );

};


const TestPage = () => {

    const [selectedUser, setSelectedUser] = useState(null);
    const [activeTab, setActiveTab] = useState(0); // Track active tab

    const handleEdit = (user) => {
        setSelectedUser(user);
        setActiveTab(0);
    };
    const handleUserUpdated = () => {
        setSelectedUser(null);
    };
    return (<TabbedFormComponent
        formContent={(switchToFormTab) => (
            <UserForm
                selectedUser={selectedUser}
                onUserUpdated={handleUserUpdated} // Pass the callback here
                switchToFormTab={switchToFormTab}
            />
        )}
        dataTableComponent={(switchToFormTab) => (
            <UserListTable
                onSelectUser={setSelectedUser}
                switchToFormTab={switchToFormTab}
            />
        )}
    />)
};

export default TestPage;
